import { TeamReportDataTableProps } from "../TeamReportDataTable/types";
import type { ReportWindow } from "../OverviewPage/Header/types";
import { windowRange } from "../reportWindow";

export interface HeadshotEntry {
  playerId: number;
  playerName: string;
  value: number;
  percentile: number;
}

// Crash rate over expectation on fewer chances than this is noise (last
// season's floor, kept).
export const CRASH_MIN_CHANCES = 10;
export { GAME_SAMPLE_FLOOR, GAME_SHOT_SAMPLE_FLOOR } from "../constants";

/**
 * The strip's players: the best three and worst three, or everyone (best
 * first) when six or fewer qualify — slicing top/bottom three off a short list
 * showed the same player twice. `rankBy` picks what "best" reads, for both who
 * appears and the order: "percentile" (the default), or "value" when the
 * printed value is itself higher = better and its percentiles aren't on one
 * scale (the crash cell's rank within each position group, IV1 2026-10-11).
 * Ties fall to the percentile, then the name.
 */
export function pickHeadshots(
  table: TeamReportDataTableProps | undefined,
  metric: string,
  minChances = 0,
  rankBy: "percentile" | "value" = "percentile",
): HeadshotEntry[] {
  const key = (row: { cells: Record<string, any> }, by: "percentile" | "value") =>
    Number(row.cells[metric]?.[by] ?? 0);
  const ranked = (table?.rows ?? [])
    .filter((row) => {
      const cell = row.cells[metric];
      if (cell?.value == null || cell.percentile == null) return false;
      return (row.cells["Poss"]?.value ?? 0) >= minChances;
    })
    .sort(
      (a, b) =>
        key(b, rankBy) - key(a, rankBy) ||
        key(b, "percentile") - key(a, "percentile") ||
        String(a.name).localeCompare(String(b.name)),
    )
    .map((row) => ({
      playerId: row.playerId ?? 0,
      playerName: String(row.name),
      value: row.cells[metric]?.value as number,
      percentile: row.cells[metric]?.percentile as number,
    }));
  return ranked.length <= 6
    ? ranked
    : [...ranked.slice(0, 3), ...ranked.slice(-3)];
}

const PUBLIC_SEASON_TYPES: Record<string, string> = {
  PlayIn: "Play-In",
  Playoffs: "Playoffs",
  "Regular Season": "Regular Season",
  "Pre Season": "Preseason",
  Preseason: "Preseason",
  "NBA Cup": "NBA Cup",
};

/** The game's own scope line ("2025-26 · Play-In"): game blobs carry the
 * NBA schedule row, not the report season. */
export function gameScope(game: any): string[] | undefined {
  if (!game) return undefined;
  const type = PUBLIC_SEASON_TYPES[game.season_type] ?? game.season_type;
  return [game.season_year, type, game.series_text].filter(Boolean);
}

/** "APR 14, 2026 · AT PHX · W 114–110": the footer and page-2 context line. */
export function gameLine(gameInfo: any, date: string): string {
  if (!gameInfo) return date;
  return [
    date,
    `${gameInfo.isHome ? "vs" : "at"} ${gameInfo.opponent}`,
    `${gameInfo.result} ${gameInfo.teamScore}–${gameInfo.opponentScore}`,
  ].join(" · ");
}

// Regulation quarters are 12 minutes, overtimes 5.
const periodMinutes = (period: number) => (period <= 4 ? 12 : 5);
const periodLabel = (period: number) =>
  period <= 4 ? `Q${period}` : period === 5 ? "OT" : `${period - 4}OT`;

/** "Garbage time from 6:54 Q4 (POR 115–102)" — our score first; a start at
 * the period's full clock reads "start of Q4". null when the game never got
 * there (or the blob predates the field). */
export function garbageLine(gameInfo: any, teamAbbrev: string): string | null {
  const g = gameInfo?.garbage;
  if (!g) return null;
  const ours = gameInfo.isHome ? g.homeScore : g.awayScore;
  const theirs = gameInfo.isHome ? g.awayScore : g.homeScore;
  const atStart =
    g.clockMinutes === periodMinutes(g.period) && g.clockSeconds === 0;
  const when = atStart
    ? `start of ${periodLabel(g.period)}`
    : `${g.clockMinutes}:${String(g.clockSeconds).padStart(2, "0")} ${periodLabel(g.period)}`;
  return `Garbage time from ${when} (${teamAbbrev} ${ours}–${theirs})`;
}

/** Game minutes elapsed when garbage time began (overtimes count five each),
 * on the rotation map's minute scale. */
export function garbageMinute(gameInfo: any): number | null {
  const g = gameInfo?.garbage;
  if (!g) return null;
  const before =
    g.period <= 4 ? (g.period - 1) * 12 : 48 + (g.period - 5) * 5;
  const remaining = g.clockMinutes + g.clockSeconds / 60;
  return before + periodMinutes(g.period) - remaining;
}

/** "2026-27 REGULAR SEASON · 2 GP · AS OF OCT 26, 2026": a season report's footer line. Page 2's running header
 * leaves the date out (`asOf` false): its line doesn't wrap, and with the date it ran past the paper (page 1's band
 * and the footer carry it). */
export function seasonLine(
  header: {
    season?: number | string;
    season_type?: string;
    asOfDate?: string;
    conferenceStandingsActual?: { wins: number; losses: number } | null;
    window?: ReportWindow | null;
  },
  asOf = true,
): string {
  const s = Number(header.season);
  const year = Number.isFinite(s) ? `${s}-${String(s + 1).slice(-2)}` : "";
  const type = PUBLIC_SEASON_TYPES[header.season_type ?? ""] ?? header.season_type;
  const c = header.conferenceStandingsActual;
  const w = header.window;
  // a window report: "2025-26 LAST 10 · MAR 23 – APR 12 · 10 GP"
  return [
    [year, w ? w.label : type].filter(Boolean).join(" "),
    w ? windowRange(w) || undefined : undefined,
    w ? `${w.games} GP` : c ? `${c.wins + c.losses} GP` : undefined,
    asOf && header.asOfDate ? `As of ${header.asOfDate}` : undefined,
  ]
    .filter(Boolean)
    .join(" · ");
}
